<script setup>
import { nextTick, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import VisionInput from './VisionInput.vue';
import VisionEmptyState from './VisionEmptyState.vue';
import VisionLoader from './VisionLoader.vue';
import VisionAssistantMessage from './VisionAssistantMessage.vue';
import CopilotAgentMessage from 'dashboard/components-next/copilot/CopilotAgentMessage.vue';
import CopilotThinkingGroup from 'dashboard/components-next/copilot/CopilotThinkingGroup.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const props = defineProps({
  messages: {
    type: Array,
    default: () => [],
  },
  contactCpf: {
    type: String,
    default: '',
  },
  // { id_contrato, label } escolhido na conversa — entra nas perguntas prontas.
  selectedContract: {
    type: Object,
    default: null,
  },
  // Expandir/recolher a largura do painel — quem hospeda decide se oferece
  // (a conversa sim; o painel do Studio já tem largura própria).
  expandable: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  // Props do modo voz (voiceSupported/voiceState/...) removidas enquanto o
  // VisionVoiceBar está oculto.
});

const emit = defineEmits(['sendMessage', 'reset', 'close', 'toggleExpand']);

const { t } = useI18n();

const chatContainer = ref(null);

const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

// Agrupa `assistant_thinking` consecutivos num bloco colapsável (mesma lógica do Copilot).
const groupedMessages = computed(() => {
  const result = [];
  let thinkingGroup = [];
  props.messages.forEach(message => {
    if (message.message_type === 'assistant_thinking') {
      thinkingGroup.push(message);
    } else {
      if (thinkingGroup.length > 0) {
        result.push({
          id: thinkingGroup[0].id,
          message_type: 'thinking_group',
          messages: thinkingGroup,
        });
        thinkingGroup = [];
      }
      result.push(message);
    }
  });
  if (thinkingGroup.length > 0) {
    result.push({
      id: thinkingGroup[0].id,
      message_type: 'thinking_group',
      messages: thinkingGroup,
    });
  }
  return result;
});

const isLastMessageFromAssistant = computed(() => {
  const last = groupedMessages.value[groupedMessages.value.length - 1];
  return last?.message_type === 'assistant';
});

const hasMessages = computed(() => props.messages.length > 0);

const visionButtons = computed(() => {
  const buttons = [];
  if (props.expandable) {
    buttons.push({
      key: 'expand',
      icon: props.expanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2',
      tooltip: props.expanded ? t('VISION.COLLAPSE') : t('VISION.EXPAND'),
    });
  }
  if (hasMessages.value) {
    buttons.push({
      key: 'reset',
      icon: 'i-lucide-refresh-ccw',
      tooltip: t('VISION.RESET'),
    });
  }
  return buttons;
});

const sendMessage = message => emit('sendMessage', message);

const handleSidebarAction = action => {
  if (action === 'reset') emit('reset');
  if (action === 'expand') emit('toggleExpand');
};

watch(
  () => props.messages,
  () => {
    scrollToBottom();
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col h-full text-sm leading-6 tracking-tight w-full">
    <SidebarActionsHeader
      :title="$t('VISION.TITLE')"
      :buttons="visionButtons"
      @click="handleSidebarAction"
      @close="emit('close')"
    />
    <div
      ref="chatContainer"
      class="flex-1 flex px-4 py-4 overflow-y-auto items-start"
    >
      <div v-if="hasMessages" class="space-y-6 flex-1 flex flex-col w-full">
        <template v-for="item in groupedMessages" :key="item.id">
          <CopilotAgentMessage
            v-if="item.message_type === 'user'"
            :message="item.message"
          />
          <VisionAssistantMessage
            v-else-if="item.message_type === 'assistant'"
            :message="item.message"
          />
          <CopilotThinkingGroup
            v-else
            :messages="item.messages"
            :default-collapsed="isLastMessageFromAssistant"
          />
        </template>

        <VisionLoader v-if="!isLastMessageFromAssistant" />
      </div>
      <VisionEmptyState
        v-else
        :contact-cpf="contactCpf"
        :selected-contract="selectedContract"
        @use-suggestion="sendMessage"
      />
    </div>

    <div class="mx-3 mt-px mb-2 space-y-2">
      <!-- Modo voz (VisionVoiceBar) oculto por enquanto -->
      <VisionInput class="w-full" @send="sendMessage" />
    </div>
  </div>
</template>
